import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Loader2, Pencil, Trash2, UserPlus, X } from "lucide-react";
import toast from "react-hot-toast";
import { fetchAssistants, deleteAssistant } from "../../Store/Actions/AssistantActions";

const PERMISSION_LABELS = {
  events: "Create/Update Events",
  resources: "Create/Update Resources",
  collaboration: "Collaboration Workspace",
  exploreNonprofits: "Explore Nonprofits",
  inquiries: "TCC Plus Support",
  personalSpace: "Personal Space",
  notifications: "Notifications",
};

const VISIBLE_CHIP_COUNT = 2;

const PermissionChips = ({ permissions }) => {
  const active = Object.entries(permissions)
    .filter(([, v]) => v)
    .map(([k]) => PERMISSION_LABELS[k] || k);

  if (active.length === 0)
    return <span className="text-xs text-gray-400 italic">None</span>;

  const visible = active.slice(0, VISIBLE_CHIP_COUNT);
  const overflow = active.length - VISIBLE_CHIP_COUNT;

  return (
    <div className="flex flex-wrap gap-1.5">
      {visible.map((label) => (
        <span
          key={label}
          className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full font-popinsMedium whitespace-nowrap"
        >
          {label}
        </span>
      ))}
      {overflow > 0 && (
        <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full font-popinsMedium">
          +{overflow}
        </span>
      )}
    </div>
  );
};

const AssistantsPage = () => {
  const dispatch = useDispatch();
  const { list, loading, deleteLoading } = useSelector((s) => s.assistants);
  const [pendingDelete, setPendingDelete] = useState(null); // { id, email }

  useEffect(() => {
    dispatch(fetchAssistants());
  }, [dispatch]);

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteAssistant(pendingDelete.id));
      toast.success("Assistant removed");
    } catch {
      toast.error("Failed to remove assistant");
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <div className="p-6">
      {/* Delete confirmation modal */}
      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-[90%] max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-popinsSemiBold text-lg">Remove Assistant</h2>
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deleteLoading}
                className="text-black/40 hover:text-black/70"
              >
                <X size={20} />
              </button>
            </div>
            <p className="font-popinsRegular text-sm text-black/60 mb-6 text-center">
              Are you sure you want to remove
              <span className="font-popinsMedium text-black/80 block mt-1 mb-1">
                {pendingDelete.email}
              </span>
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setPendingDelete(null)}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-md border border-border font-popinsMedium text-sm text-black/60 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="px-4 py-2 rounded-md bg-red-500 text-white font-popinsMedium text-sm hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {deleteLoading && <Loader2 size={14} className="animate-spin" />}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="font-popinsBold text-2xl">Team Access</h1>
        <Link
          to="/dashboard/assistants/add"
          className="flex items-center gap-2 bg-themeColor text-white px-4 py-2 rounded-lg font-popinsSemiBold text-sm hover:opacity-90 transition"
        >
          <UserPlus className="size-4" />
          Add Assistant
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-16 text-textColor">Loading…</div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 text-textColor">
          <p className="font-popinsSemiBold text-lg mb-2">No assistants yet</p>
          <p className="text-sm">Add an assistant to let them help manage your nonprofit account.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-5 py-3 font-popinsSemiBold text-textColor text-xs uppercase tracking-wide">
                  Username
                </th>
                <th className="px-5 py-3 font-popinsSemiBold text-textColor text-xs uppercase tracking-wide">
                  Permissions
                </th>
                <th className="px-5 py-3 font-popinsSemiBold text-textColor text-xs uppercase tracking-wide text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {list.map((assistant) => (
                <tr key={assistant.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-popinsMedium text-darkTheme truncate max-w-xs">
                      {assistant.email}
                    </p>
                    <p className="text-xs text-textColor mt-0.5">
                      Added {new Date(assistant.createdAt).toLocaleDateString()}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    <PermissionChips permissions={assistant.permissions} />
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/dashboard/assistants/${assistant.id}/edit`}
                        className="inline-flex items-center gap-1.5 text-xs font-popinsSemiBold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition"
                      >
                        <Pencil className="size-3.5" />
                        Edit
                      </Link>
                      <button
                        onClick={() => setPendingDelete({ id: assistant.id, email: assistant.email })}
                        className="inline-flex items-center gap-1.5 text-xs font-popinsSemiBold text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition"
                      >
                        <Trash2 className="size-3.5" />
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AssistantsPage;
